import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { CacheService } from '../../services/cache.service';
import { AvailableCaches } from '../_models/ignatius.models';
import {
  ArrangeTable,
  ArrangeTableFailed,
  ArrangeTableSuccess,
  CheckTableHasData,
  CheckTableHasDataFailed,
  CheckTableHasDataSuccess,
  CreateTable,
  CreateTableFailed,
  CreateTableSuccess,
  DeleteTable,
  DeleteTableFailed,
  DeleteTableSuccess,
  LoadAllTables,
  LoadAllTablesFailed,
  LoadAllTablesSuccess,
  LoadNavigation,
  LoadNavigationFailed,
  LoadNavigationSuccess,
  LoadPageAndDashboard,
  LoadPageAndDashboardFailed,
  LoadPageAndDashboardSuccess,
  LoadTables,
  LoadTablesFailed,
  LoadTablesSuccess,
  TableActionTypes,
  UpdatePagesAndDashboard,
  UpdatePagesAndDashboardFailed,
  UpdatePagesAndDashboardSuccess,
  UpdateTable,
  UpdateTableFailed,
  UpdateTableSuccess,
  ValidateTableName,
  ValidateTableNameFailed,
  ValidateTableNameSuccess,
} from '../_actions/table.actions';
import { getAllTables, getNavigation, getTables } from '../_selectors/table.selector';
import { TableService } from '../_services/table.service';
import {
  ITable,
  Navigation,
  UpdateTableResponse,
  ValidateTablePayload,
} from '../_models/table.models';

@Injectable()
export class TableEffect {
  constructor(
    private actions: Actions,
    private service: TableService,
    private store: Store,
    private cache: CacheService
  ) {}

  /**
   * Get application
   */
  navigation: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadNavigation>(TableActionTypes.LOAD_NAVIGATION),
      withLatestFrom(this.store.pipe(select(getNavigation))),
      mergeMap(([action, navigation]) => {
        if (
          (navigation as any)[action.applicationId] &&
          this.cache.getCacheState(AvailableCaches.NAVIGATION) &&
          !action.force
        )
          return of(
            new LoadNavigationSuccess(
              action.applicationId,
              (navigation as any)[action.applicationId]
            )
          );
        return this.service.getNavigation(action.applicationId).pipe(
          map(
            (payload: Array<Navigation>) =>
              new LoadNavigationSuccess(action.applicationId, payload)
          ),
          catchError((err) => of(new LoadNavigationFailed(err)))
        );
      })
    )
  );

  /**
   * Update table name
   */
  updateTableName: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<UpdateTable>(TableActionTypes.UPDATE_TABLE),
      mergeMap((action) => {
        return this.service.updateTable(action.payload).pipe(
          map((payload: UpdateTableResponse) => {
            const instance = new UpdateTableSuccess(
              action.payload.ApplicationKey,
              action.payload.Key,
              payload
            );
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new UpdateTableFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Delete table name
   */
  deleteTableName: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<DeleteTable>(TableActionTypes.DELETE_TABLE),
      mergeMap((action) => {
        return this.service.deleteTable(action.tableId).pipe(
          map(() => {
            const instance = new DeleteTableSuccess(
              action.applicationId,
              action.tableId
            );
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new DeleteTableFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Create table
   */
  createTable: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<CreateTable>(TableActionTypes.CREATE_TABLE),
      mergeMap((action) => {
        return this.service.createTable(action.payload).pipe(
          map((payload: UpdateTableResponse) => {
            const instance = new CreateTableSuccess(
              action.payload.ApplicationKey,
              payload
            );
            instance.refId = action.refId;
            this.store.dispatch(
              new LoadNavigation(action.payload.ApplicationKey, true)
            );
            return instance;
          }),
          catchError((err) => {
            const instance = new CreateTableFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Arrange table
   */
  arrangeTable: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<ArrangeTable>(TableActionTypes.ARRANGE_TABLE),
      mergeMap((action) => {
        return this.service.arrangeTable(action.payload).pipe(
          map((payload: any) => {
            const instance = new ArrangeTableSuccess(payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new ArrangeTableFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Validate table name
   */
  validateName: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<ValidateTableName>(TableActionTypes.VALIDATE_TABLE_NAME),
      mergeMap((action) => {
        return this.service
          .validateName(action.applicationId, action.name)
          .pipe(
            map(
              (payload: ValidateTablePayload) =>
                new ValidateTableNameSuccess(action.applicationId, payload)
            ),
            catchError((err) => of(new ValidateTableNameFailed(err)))
          );
      })
    )
  );

  /**
   * Get tables
   */
  tables: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadTables>(TableActionTypes.LOAD_TABLES),
      withLatestFrom(this.store.pipe(select(getTables))),
      mergeMap(([action, table]) => {
        if (
          (table as any)[action.applicationId] &&
          this.cache.getCacheState(AvailableCaches.TABLES) &&
          !action.force
        )
          return of(
            new LoadTablesSuccess(
              action.applicationId,
              (table as any)[action.applicationId]
            )
          );
        return this.service.getTables(action.applicationId).pipe(
          map(
            (payload: Array<ITable>) =>
              new LoadTablesSuccess(action.applicationId, payload)
          ),
          catchError((err) => of(new LoadTablesFailed(err, action.applicationId)))
        );
      })
    )
  );
  /**
   * Get all tables
   */
  allTables: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadAllTables>(TableActionTypes.LOAD_ALL_TABLES),
      withLatestFrom(this.store.pipe(select(getAllTables))),
      mergeMap(([action, table]) => {
        if (
          (table as any)[action.applicationId] &&
          this.cache.getCacheState(AvailableCaches.ALLTABLES) &&
          !action.force
        )
          return of(
            new LoadAllTablesSuccess(
              action.applicationId,
              (table as any)[action.applicationId]
            )
          );
        return this.service.getAllTables(action.applicationId).pipe(
          map(
            (payload: Array<ITable>) =>
              new LoadAllTablesSuccess(action.applicationId, payload)
          ),
          catchError((err) => of(new LoadAllTablesFailed(err, action.applicationId)))
        );
      })
    )
  );

  getPageAndDashboard: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadPageAndDashboard>(TableActionTypes.LOAD_PAGES_DASHBOARD),
      // withLatestFrom(this.store.pipe(select(getTables))),
      mergeMap((action: any) => {
        return this.service.getPages(action.tableId).pipe(
          map(
            (payload: any) =>
              new LoadPageAndDashboardSuccess(action.tableId, payload)
          ),
          catchError((err) => of(new LoadPageAndDashboardFailed(err)))
        );
      })
    )
  );

  updatePageAndDashboard: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<UpdatePagesAndDashboard>(TableActionTypes.UPDATE_PAGES_DASHBOARD),
      mergeMap((action: any) => {
        return this.service.updatePagesAndDashboard(action.payload).pipe(
          map(() => new UpdatePagesAndDashboardSuccess()),
          catchError((err) => of(new UpdatePagesAndDashboardFailed(err)))
        );
      })
    )
  );

  /**
   * Check if table has data
   * returns TRUE if table has data
   * returns FALSE if table is empty
   */
  tableHasData: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<CheckTableHasData>(TableActionTypes.TABLE_HAS_DATA),
      mergeMap((action) => {
        return this.service.tableHasData(action.tableId).pipe(
          map(
            (payload: boolean) =>
              new CheckTableHasDataSuccess(action.tableId, payload)
          ),
          catchError((err) => of(new CheckTableHasDataFailed(err)))
        );
      })
    )
  );
}
